import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VocabData, EMPTY_VOCAB, BASE_ROLE_TYPES, BASE_ARCHETYPES } from '../constants/vocab';
import { apiPost } from '../api';

// ── Exported types (imported by RelationshipPage) ─────────────────────────────

export interface PanelEntity {
  entityId: string;
  entityType: 'character' | 'location' | 'event';
  name: string;
  description: string;
  imageUrl?: string;
  metadata: Record<string, unknown>;
  bookIds: string[];
  seriesIds: string[];
}

export interface EntityUpdate {
  name?: string;
  description?: string;
  imageUrl?: string;
  metadata?: Record<string, unknown>;
  bookIds?: string[];
  seriesIds?: string[];
  /* The book or series the user just unticked. POST /<entity> only ever adds the
     bookIds and seriesIds it is sent, so taking one away is said outright, and only for
     the box actually unticked, never worked out by comparing copies that may be stale. (#964) */
  removeBookIds?: string[];
  removeSeriesIds?: string[];
}

interface EntitySidePanelProps {
  entity: PanelEntity;
  characters: PanelEntity[];
  locations: PanelEntity[];
  books: { entityId: string; title: string }[];
  seriesList: { entityId: string; title: string }[];
  onClose: () => void;
  onSave: (entityId: string, updates: EntityUpdate) => Promise<void>;
  onDelete: (entityId: string) => void;
  onAutoRelate: (eventId: string, targetId: string, type: string) => Promise<void>;
  onCreateEntity: (entityType: PanelEntity['entityType'], name: string) => Promise<void>;
  onImageUploaded?: (entityId: string, viewUrl: string) => void;
  vocab?: VocabData;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const ENTITY_COLORS: Record<string, string> = {
  character: '#196773',
  location: '#5CA383',
  event: '#c97a1e',
};

const ENTITY_LABELS: Record<string, string> = {
  character: 'Character',
  location: 'Location',
  event: 'Event',
};


// ── Reusable field renderers ───────────────────────────────────────────────────

interface FieldProps {
  label: string;
  value: string;
  maxLength: number;
  onChange: (val: string) => void;
}

const TextField: React.FC<FieldProps> = ({ label, value, maxLength, onChange }) => (
  <div className="entity-panel-field">
    <label>{label}
      <input type="text" maxLength={maxLength} value={value} onChange={e => onChange(e.target.value)} />
    </label>
  </div>
);

const TextareaField: React.FC<FieldProps> = ({ label, value, maxLength, onChange }) => (
  <div className="entity-panel-field">
    <label>{label}
      <textarea maxLength={maxLength} value={value} onChange={e => onChange(e.target.value)} />
    </label>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────

const EntitySidePanel: React.FC<EntitySidePanelProps> = ({
  entity, characters, locations, books, seriesList,
  onClose, onSave, onDelete, onAutoRelate, onCreateEntity,
  onImageUploaded,
  vocab = EMPTY_VOCAB,
}) => {
  const [localName, setLocalName] = useState(entity.name);
  const [localDesc, setLocalDesc] = useState(entity.description);
  const [localImageUrl, setLocalImageUrl] = useState(entity.imageUrl ?? '');
  const [localBookIds, setLocalBookIds] = useState<string[]>(entity.bookIds);
  const [localSeriesIds, setLocalSeriesIds] = useState<string[]>(entity.seriesIds);
  const [localMeta, setLocalMeta] = useState<Record<string, unknown>>(entity.metadata ?? {});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdates = useRef<EntityUpdate>({});
  /* The entity the pending edits were made on. The panel is not remounted when another
     entity is opened in it, so an edit on one and a keystroke on the next, inside the
     debounce, merged into one save written to the second; and closing inside it dropped
     the edit. Pending edits now go to this entity, at once, on a switch or close. (#965) */
  const pendingFor = useRef('');
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  /* Creating is a POST, and until it resolves entity.entityId is still '' - so
     isCreating is still true and a second blur creates a second entity. Reachable:
     press Enter, click back into the field while the request is in flight, press
     Enter again. There is no progress indicator during create, so on a slow
     connection that is exactly what someone does. (#782) */
  const creating = useRef(false);
  /* Set before focus leaves the name field, so onBlur can tell "the user moved on
     within the panel" from "the user is abandoning this entity". Without it there is
     no way to back out of a new entity: blur is blur, and it commits. (#783) */
  const abandoning = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Send any pending edits now, to the entity they were made on. The status line is left
     alone: by now the panel may be showing another entity, or be gone. */
  const flushPending = useCallback(() => {
    if (!debounceTimer.current) return;
    clearTimeout(debounceTimer.current);
    debounceTimer.current = null;
    const id = pendingFor.current;
    const updates = pendingUpdates.current;
    pendingUpdates.current = {};
    onSaveRef.current(id, updates).catch(e => console.error('Failed to save edits to', id, e));
  }, []);

  useEffect(() => {
    flushPending();
    creating.current = false;
    abandoning.current = false;
    setLocalName(entity.name);
    setLocalDesc(entity.description);
    setLocalImageUrl(entity.imageUrl ?? '');
    setLocalBookIds(entity.bookIds);
    setLocalSeriesIds(entity.seriesIds);
    setLocalMeta(entity.metadata ?? {});
    setConfirmDelete(false);
    setSaveStatus('idle');
    setUploadError('');
  }, [entity.entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* On delete, edits still waiting are for a record about to go. Sending them after the
     delete, as closing the panel would, writes it back: the POST is an upsert. */
  const dropPending = () => {
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null; }
    pendingUpdates.current = {};
  };

  useEffect(() => () => {
    flushPending();
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, [flushPending]);

  const markSaved = () => {
    setSaveStatus('saved');
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveStatus('idle'), 2000);
  };

  const scheduleUpdate = (partial: EntityUpdate) => {
    if (isCreating) return;
    pendingFor.current = entity.entityId;
    pendingUpdates.current = {
      ...pendingUpdates.current,
      ...partial,
      ...(partial.metadata
        ? { metadata: { ...(pendingUpdates.current.metadata ?? {}), ...(partial.metadata as Record<string, unknown>) } }
        : {}),
    };
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSaveStatus('saving');
    debounceTimer.current = setTimeout(async () => {
      debounceTimer.current = null;
      const updates = { ...pendingUpdates.current };
      pendingUpdates.current = {};
      try { await onSave(pendingFor.current, updates); markSaved(); }
      catch { setSaveStatus('error'); }
    }, 1000);
  };

  const saveNow = async (updates: EntityUpdate) => {
    if (isCreating) return;
    /* A save from an earlier render, such as an image upload that finishes after another
       entity was opened, must not take that entity's waiting edits along: they go to it. */
    if (pendingFor.current !== entity.entityId) flushPending();
    /* Cancel the debounce this is overtaking, or it fires the same write again a
       second later. Pressing Enter to save a rename is always inside the 1s window,
       so without this every rename is two PUTs; clicking away merely got lucky
       sometimes. Pending edits are merged rather than dropped - a bare clearTimeout
       would silently lose a description typed just before. (#782) */
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null; }
    const merged = { ...pendingUpdates.current, ...updates };
    pendingUpdates.current = {};
    setSaveStatus('saving');
    try { await onSave(entity.entityId, merged); markSaved(); }
    catch { setSaveStatus('error'); }
  };

  const handleMetaChange = (key: string, value: unknown) => {
    const next = { ...localMeta, [key]: value };
    setLocalMeta(next);
    scheduleUpdate({ metadata: { [key]: value } });
  };

  const handleParticipantCharChange = async (charId: string, checked: boolean) => {
    const prev = (localMeta.participantCharacterIds as string[]) ?? [];
    const next = checked ? [...prev, charId] : prev.filter(id => id !== charId);
    const newMeta = { ...localMeta, participantCharacterIds: next };
    setLocalMeta(newMeta);
    await saveNow({ metadata: newMeta });
    if (checked) {
      try { await onAutoRelate(entity.entityId, charId, 'participant'); } catch { /* non-fatal */ }
    }
  };

  const handleParticipantLocChange = async (locId: string) => {
    const newMeta = { ...localMeta, participantLocationId: locId || null };
    setLocalMeta(newMeta);
    await saveNow({ metadata: newMeta });
    if (locId) {
      try { await onAutoRelate(entity.entityId, locId, 'takes place at'); } catch { /* non-fatal */ }
    }
  };

  const handleImageUpload = useCallback(async (file: File) => {
    setUploading(true);
    setUploadError('');
    try {
      const res = await apiPost('/entities/upload-url', { entityId: entity.entityId, contentType: file.type || 'image/png' });
      const { uploadUrl, imageKey, viewUrl } = await res.body.json() as { uploadUrl: string; imageKey: string; viewUrl: string };
      await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'image/png' }, body: file });
      setLocalImageUrl(viewUrl);
      await saveNow({ imageUrl: imageKey });
      onImageUploaded?.(entity.entityId, viewUrl);
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [entity.entityId, onImageUploaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const color = ENTITY_COLORS[entity.entityType];
  const typeLabel = ENTITY_LABELS[entity.entityType];
  const isCreating = entity.entityId === '';

  /* Nothing is being "saved" while creating - the entity does not exist yet - so a
     failed POST reporting "Save failed" tells the user the wrong thing about the
     thing they just did. (#782) */
  const statusText = saveStatus === 'saving' ? (isCreating ? 'Creating…' : 'Saving…')
    : saveStatus === 'saved' ? 'Saved ✓'
    : saveStatus === 'error' ? (isCreating ? 'Could not create' : 'Save failed')
    : '';

  const m = localMeta;

  const allRoles = [...BASE_ROLE_TYPES, ...vocab.customRoleTypes];
  const storedRole = (m.roleType as string) ?? '';
  /* A role that is not in the list, whether none was set or a custom one was since
     deleted, shows "— not set —", as Archetype does. Showing Protagonist for it meant
     picking Protagonist fired no change, so it was never saved. (#956, #962) */
  const displayRole = allRoles.includes(storedRole) ? storedRole : '';

  const allArchetypes = [...BASE_ARCHETYPES, ...vocab.customArchetypes];
  const storedArchetype = (m.archetype as string) ?? '';
  const displayArchetype = allArchetypes.includes(storedArchetype) ? storedArchetype : '';

  return (
    <div className="entity-panel">

      {/* Header */}
      <div className="entity-panel-header">
        <div className="entity-panel-header-top">
          <span className="entity-panel-type-badge" style={{ background: color }}>{typeLabel}</span>
          <span className="entity-panel-save-status">{statusText}</span>
          {/* onMouseDown, not onClick: pressing the mouse moves focus off the name
              field, so the input's onBlur runs before any click handler could. Closing
              a half-typed entity used to create it on the way out.
              The mouse path only — tabbing away fires blur before this button ever
              receives focus, so Escape is the keyboard way out. (#783) */}
          <button
            className="entity-panel-close"
            aria-label="Close panel"
            onMouseDown={e => { if (e.button === 0 && isCreating) abandoning.current = true; }}
            onClick={onClose}
          >✕</button>
        </div>
        <input
          className="entity-panel-name-input"
          value={localName}
          maxLength={100}
          placeholder={isCreating ? `Name this ${entity.entityType}…` : ''}
          autoFocus={isCreating}
          style={{ borderBottomColor: color }}
          /* Releases the abandon latch. Press the mouse on the close button, drag off
             it and release, and no click follows — the latch would stay set and every
             later create would be swallowed silently, on a panel that never unmounts
             because entityId is still ''. Clearing on focus makes it self-healing
             against any path like that. (#783) */
          onFocus={() => { abandoning.current = false; }}
          onChange={e => {
            setLocalName(e.target.value);
            if (!isCreating) scheduleUpdate({ name: e.target.value });
          }}
          onBlur={() => {
            // The panel is being closed, not merely left. Committing here is the one
            // thing the user has just said they do not want.
            if (abandoning.current) return;
            const name = localName.trim();
            if (!name) return;
            if (isCreating) {
              if (creating.current) return;
              creating.current = true;
              setSaveStatus('saving');
              // Not awaited anywhere else, so a rejected POST was an unhandled
              // rejection and the user saw nothing at all - the same "Enter did
              // nothing" symptom this issue is about, on the failure path.
              onCreateEntity(entity.entityType, name).catch(() => {
                creating.current = false;
                setSaveStatus('error');
              });
            } else if (name !== entity.name) {
              saveNow({ name });
            }
          }}
          /* Enter commits by blurring rather than by calling onCreateEntity itself, so
             onBlur stays the single commit path and the two cannot both fire. It also
             makes key auto-repeat harmless: after the first blur the input is no longer
             the focus target, so held keys never reach this handler. Same approach as
             the storyboard frame label in FrameNode.

             Escape abandons rather than committing. FrameNode binds Escape to a plain
             blur, which for a local label just commits it - fine there, wrong here,
             where blur means a POST. A key people press to back out must do exactly
             that, so it marks the panel as abandoning and closes. (#782, #783) */
          onKeyDown={e => {
            /* An Enter that confirms an IME candidate belongs to the IME, not to us.
               Committing on it would create the entity from half-composed text, which
               matters in a writing tool - CJK names are not an edge case. Checked
               before preventDefault, which can itself disturb the confirmation. */
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === 'Escape') {
              e.preventDefault();
              if (isCreating) {
                abandoning.current = true;
              } else {
                /* Blur FIRST. onClose unmounts the panel, and browsers do not fire
                   blur for an element that is removed from the DOM — so without this
                   the rename just typed would reach the server only through the
                   unmount flush, untrimmed and unchecked for an empty name. Escape
                   previously did nothing at all. (#783, #965) */
                e.currentTarget.blur();
              }
              onClose();
              return;
            }
            if (e.key !== 'Enter') return;
            // No form in this tree today; this only guards against one being added.
            e.preventDefault();
            // Nothing to commit: blurring would drop focus for no reason and leave the
            // panel in create mode with the field the user is typing in deselected.
            if (!localName.trim()) return;
            e.currentTarget.blur();
          }}
        />
      </div>

      {/* Body — dims when in create mode */}
      <div style={{ pointerEvents: isCreating ? 'none' : undefined, opacity: isCreating ? 0.4 : undefined }}>

      {/* Core — all types */}
      <div className="entity-panel-section">
        <div className="entity-panel-section-title" style={{ color }}>Core</div>
        <div className="entity-panel-field">
          <label>Description
            <textarea
              maxLength={300}
              value={localDesc}
              onChange={e => { setLocalDesc(e.target.value); scheduleUpdate({ description: e.target.value }); }}
              onBlur={() => { if (localDesc !== entity.description) saveNow({ description: localDesc }); }}
            />
          </label>
        </div>
        <div className="entity-panel-field">
          <label>Image</label>
          <div className="entity-panel-image-wrap">
            {localImageUrl && (
              <img className="entity-panel-image-thumb" src={localImageUrl} alt="entity thumbnail" />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ''; }}
            />
            <button
              className="entity-panel-image-upload-btn"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : localImageUrl ? 'Replace image' : 'Upload image'}
            </button>
            {uploadError && <span className="entity-panel-image-error">{uploadError}</span>}
          </div>
        </div>
      </div>

      {/* Character-specific sections */}
      {entity.entityType === 'character' && (<>
        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Appearance</div>
          <div className="entity-panel-field-two-col">
            <TextField label="Age" value={(m.age as string) ?? ''} maxLength={100} onChange={v => handleMetaChange('age', v)} />
            <TextField label="Build" value={(m.build as string) ?? ''} maxLength={100} onChange={v => handleMetaChange('build', v)} />
          </div>
          <TextareaField label="Distinguishing features" value={(m.distinguishingFeatures as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('distinguishingFeatures', v)} />
        </div>

        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Personality</div>
          <div className="entity-panel-field-two-col">
            <TextField label="Core traits" value={(m.coreTraits as string) ?? ''} maxLength={200} onChange={v => handleMetaChange('coreTraits', v)} />
            <TextField label="Fatal flaw" value={(m.fatalFlaw as string) ?? ''} maxLength={200} onChange={v => handleMetaChange('fatalFlaw', v)} />
          </div>
          <TextareaField label="Motivation" value={(m.motivation as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('motivation', v)} />
        </div>

        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Backstory</div>
          <TextareaField label="Origin" value={(m.origin as string) ?? ''} maxLength={500} onChange={v => handleMetaChange('origin', v)} />
          <TextareaField label="Key life events" value={(m.keyLifeEvents as string) ?? ''} maxLength={500} onChange={v => handleMetaChange('keyLifeEvents', v)} />
        </div>

        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Story Role</div>
          <div className="entity-panel-field">
            <label>Role type
              <select value={displayRole} onChange={async e => {
                const v = e.target.value;
                const newMeta = { ...localMeta, roleType: v };
                setLocalMeta(newMeta);
                await saveNow({ metadata: newMeta });
              }}>
                <option value="">— not set —</option>
                {allRoles.map(o =>
                  <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                )}
              </select>
            </label>
          </div>
          <div className="entity-panel-field">
            <label>Archetype
              <select value={displayArchetype} onChange={async e => {
                const v = e.target.value;
                const newMeta = { ...localMeta, archetype: v };
                setLocalMeta(newMeta);
                await saveNow({ metadata: newMeta });
              }}>
                <option value="">— not set —</option>
                {allArchetypes.map(o =>
                  <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                )}
              </select>
            </label>
          </div>
          <TextareaField label="Arc summary" value={(m.arcSummary as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('arcSummary', v)} />
          <TextField label="Voice / dialect notes" value={(m.voiceDialectNotes as string) ?? ''} maxLength={200} onChange={v => handleMetaChange('voiceDialectNotes', v)} />
        </div>
      </>)}

      {/* Location-specific sections */}
      {entity.entityType === 'location' && (<>
        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Geography</div>
          <div className="entity-panel-field-two-col">
            <TextField label="Climate" value={(m.climate as string) ?? ''} maxLength={100} onChange={v => handleMetaChange('climate', v)} />
            <TextField label="Terrain" value={(m.terrain as string) ?? ''} maxLength={100} onChange={v => handleMetaChange('terrain', v)} />
          </div>
          <TextareaField label="Notable landmarks" value={(m.notableLandmarks as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('notableLandmarks', v)} />
        </div>

        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Culture</div>
          <TextField label="Government / authority" value={(m.government as string) ?? ''} maxLength={200} onChange={v => handleMetaChange('government', v)} />
          <TextareaField label="Customs & beliefs" value={(m.customsBeliefs as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('customsBeliefs', v)} />
          <TextField label="Economy" value={(m.economy as string) ?? ''} maxLength={200} onChange={v => handleMetaChange('economy', v)} />
        </div>

        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>History</div>
          <TextareaField label="Founding / origin" value={(m.foundingOrigin as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('foundingOrigin', v)} />
          <TextareaField label="Current state" value={(m.currentState as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('currentState', v)} />
        </div>
      </>)}

      {/* Event-specific sections */}
      {entity.entityType === 'event' && (<>
        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Timeline</div>
          <div className="entity-panel-field-two-col">
            <TextField label="When it occurs" value={(m.whenItOccurs as string) ?? ''} maxLength={100} onChange={v => handleMetaChange('whenItOccurs', v)} />
            <TextField label="Duration" value={(m.duration as string) ?? ''} maxLength={100} onChange={v => handleMetaChange('duration', v)} />
          </div>
        </div>

        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Participants</div>
          <div className="entity-panel-field">
            <label>Characters involved</label>
            {characters.length === 0
              ? <div className="entity-panel-entity-empty">No characters yet</div>
              : <div className="entity-panel-entity-checklist">
                  {characters.map(c => (
                    <label key={c.entityId} className="entity-panel-entity-check-item">
                      <input
                        type="checkbox"
                        checked={((m.participantCharacterIds as string[]) ?? []).includes(c.entityId)}
                        onChange={e => handleParticipantCharChange(c.entityId, e.target.checked)}
                      />
                      <span>{c.name}</span>
                    </label>
                  ))}
                </div>
            }
          </div>
          <div className="entity-panel-field">
            <label>Location
              <select
                value={(m.participantLocationId as string) ?? ''}
                onChange={e => handleParticipantLocChange(e.target.value)}
              >
                <option value="">— not set —</option>
                {locations.map(l => <option key={l.entityId} value={l.entityId}>{l.name}</option>)}
              </select>
            </label>
          </div>
        </div>

        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Story Impact</div>
          <TextareaField label="Cause" value={(m.cause as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('cause', v)} />
          <TextareaField label="Consequence" value={(m.consequence as string) ?? ''} maxLength={300} onChange={v => handleMetaChange('consequence', v)} />
          <TextField label="POV character" value={(m.povCharacter as string) ?? ''} maxLength={100} onChange={v => handleMetaChange('povCharacter', v)} />
        </div>
      </>)}

      {/* Associations — books and series */}
      {(books.length > 0 || seriesList.length > 0) && (
        <div className="entity-panel-section">
          <div className="entity-panel-section-title" style={{ color }}>Associations</div>
          {books.length > 0 && (
            <div className="entity-panel-field">
              <label>Books</label>
              <div className="entity-panel-entity-checklist">
                {books.map(b => (
                  <label key={b.entityId} className="entity-panel-entity-check-item">
                    <input
                      type="checkbox"
                      checked={localBookIds.includes(b.entityId)}
                      onChange={async e => {
                        const next = e.target.checked
                          ? [...localBookIds, b.entityId]
                          : localBookIds.filter(id => id !== b.entityId);
                        setLocalBookIds(next);
                        await saveNow(e.target.checked
                          ? { bookIds: next }
                          : { bookIds: next, removeBookIds: [b.entityId] });
                      }}
                    />
                    <span>{b.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {seriesList.length > 0 && (
            <div className="entity-panel-field">
              <label>Series</label>
              <div className="entity-panel-entity-checklist">
                {seriesList.map(s => (
                  <label key={s.entityId} className="entity-panel-entity-check-item">
                    <input
                      type="checkbox"
                      checked={localSeriesIds.includes(s.entityId)}
                      onChange={async e => {
                        const next = e.target.checked
                          ? [...localSeriesIds, s.entityId]
                          : localSeriesIds.filter(id => id !== s.entityId);
                        setLocalSeriesIds(next);
                        await saveNow(e.target.checked
                          ? { seriesIds: next }
                          : { seriesIds: next, removeSeriesIds: [s.entityId] });
                      }}
                    />
                    <span>{s.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      </div>

      {/* Delete — hidden in create mode */}
      {!isCreating && (
        <div className="entity-panel-footer">
          {confirmDelete ? (
            <div className="entity-panel-delete-confirm">
              <span>Delete this {typeLabel.toLowerCase()}? This cannot be undone.</span>
              <button className="btn-secondary" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn-danger" onClick={() => { dropPending(); onDelete(entity.entityId); }}>Delete</button>
            </div>
          ) : (
            <button className="btn-danger-outline" onClick={() => setConfirmDelete(true)}>
              Delete {typeLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EntitySidePanel;
